import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {PinIcon} from '@primer/octicons-react'
import type {ReactElement} from 'react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueCommentPinnedEventNode, IssueCommentUnpinnedEventNode} from './issue-viewer-view-query-types'
import {IssueCommentCopyRendering, IssueCommentLink} from './IssueCommentPinShared'
import {RolledupIssueCommentPinEvent} from './RolledupIssueCommentPinEvent'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(IssueTimelineItem): widens nullable fields so still-Relay parent data is assignable.
// Remove when IssueTimelineItem leaves Relay.
export type IssueCommentPinnedEventFragment = RelayCompat<
  Pick<IssueCommentPinnedEventNode, 'actor' | 'createdAt' | 'databaseId' | 'issueComment'>
>

type IssueCommentPinnedEventProps = {
  queryRef: IssueCommentPinnedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: Record<string, Array<IssueCommentPinnedEventFragment | IssueCommentUnpinnedEventFragment>>
}

export type IssueCommentUnpinnedEventFragment = RelayCompat<
  Pick<IssueCommentUnpinnedEventNode, 'actor' | 'createdAt' | 'databaseId' | 'issueComment'>
>

const getPinnedEventKey = (queryRef: IssueCommentPinnedEventFragment) =>
  [queryRef.databaseId ?? 'missing-db-id', queryRef.issueComment?.id ?? 'missing-comment-id', queryRef.createdAt].join(
    ':',
  )

export function IssueCommentPinnedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: IssueCommentPinnedEventProps): React.ReactElement {
  const {actor, createdAt, databaseId} = queryRef

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PinIcon}
    >
      <TimelineRow.Main>
        <span data-testid="issue-comment-pinned-copy">
          {rollupGroup ? (
            <RolledupIssueCommentPinEvent rollupGroup={rollupGroup} />
          ) : (
            <PinnedCommentRendering queryRefs={[queryRef]} rollup={false} />
          )}
        </span>{' '}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

type PinnedCommentRenderingProps = {
  queryRefs: IssueCommentPinnedEventFragment[]
  rollup: boolean
}

export const PinnedCommentRendering = ({queryRefs, rollup}: PinnedCommentRenderingProps): ReactElement | null => {
  return (
    <IssueCommentCopyRendering
      queryRefs={queryRefs}
      rollup={rollup}
      label={LABELS.timeline.issueCommentPinned.leading}
      getItemKey={getPinnedEventKey}
      renderLink={queryRef => <PinnedCommentLink queryRef={queryRef} />}
      linkPrefix="a "
    />
  )
}

export const PinnedCommentLink = ({queryRef}: {queryRef: IssueCommentPinnedEventFragment}): ReactElement => {
  return (
    <IssueCommentLink
      linkText={LABELS.timeline.issueCommentPinned.comment}
      url={queryRef.issueComment?.url}
      testId="issue-comment-pinned-link"
    />
  )
}
